import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from 'convex/react';

import { PageShell } from '@/components/layout/PageShell';
import { RequireAuth } from '@/components/auth/RequireAuth';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { DeliveryPanel } from '@/components/pipeline/panels/DeliveryPanel';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';

export function DeliveryPage() {
  return (
    <RequireAuth>
      <PageShell>
        <DeliveryInner />
      </PageShell>
    </RequireAuth>
  );
}

function DeliveryInner() {
  const { jobId } = useParams<{ jobId: string }>();
  const { token } = useAuth();
  const subArgs = useMemo((): { token: string; jobId: Id<'jobs'> } | 'skip' => {
    if (typeof token !== 'string' || !token || !jobId) return 'skip';
    return { token, jobId: jobId as Id<'jobs'> };
  }, [token, jobId]);
  const job = useQuery(api.jobs.get, subArgs);
  const artifacts = useQuery(api.artifacts.listForJob, subArgs);

  if (!jobId) return <p className="text-graphite">Missing job id in URL.</p>;
  if (job === undefined || artifacts === undefined) return <LoadingSpinner label="Loading delivery…" />;
  if (job === null) {
    return (
      <div className="space-y-3">
        <h2 className="font-serif text-display-md italic text-ink">Job not found.</h2>
        <Button asChild variant="ghost"><Link to="/projects">Back to projects</Link></Button>
      </div>
    );
  }

  const isCompleted = job.state === 'completed';

  return (
    <div className="space-y-8">
      <header className="space-y-1">
        <p className="smallcaps text-graphite">Delivery</p>
        <h1 className="font-serif text-display-md italic text-ink">
          {isCompleted ? 'Your project is ready.' : 'Not quite there yet.'}
        </h1>
        <p className="text-body text-graphite max-w-2xl">
          {isCompleted
            ? 'Below are the high-fidelity mockups LUMEN produced and the deployable code FORGE generated.'
            : <>The pipeline is still in progress. <Link to={`/jobs/${jobId}`} className="text-ochre underline">Watch the agents work →</Link></>}
        </p>
      </header>

      <ErrorBoundary>
        {isCompleted ? (
          <DeliveryPanel artifacts={artifacts} variant="standalone" />
        ) : (
          <p className="text-graphite italic">Delivery contents will appear once the pipeline completes.</p>
        )}
      </ErrorBoundary>
    </div>
  );
}
